import toast from "./toast";

const getLabel = (label) => {
  if (label.name) {
    return label.name
  }
  if (label.full_name) {
    return label.full_name
  }
  if (label.label) {
    return label.label
  }
  if (label.full_name_code) {
    return label.full_name_code
  }
}
const getValue = (element) => {
  if (element.id) {
    return element.id
  }
  if (element.key) {
    return element.key
  }
  if (element.value) {
    return element.value
  }
}
export const createSelectList = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": getValue(element), "label": getLabel(element) };
    newList.push(obj);
  });
  return newList;
}

export const createSelectResourceList = (data) => {
  let newList = []
  for (const key in data) {
    let obj = { "value": `${data[key]}` };
    newList.push(obj);
  };
  return newList;
}

export const createSelectEmploymentList = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": getValue(element), "label": element.full_name_code ? element.full_name_code : element.title };
    newList.push(obj);
  });
  return newList;
}

export const createFormattedCurrencies = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": getValue(element), "label": element.name ? `${element.name}` : element.title };
    newList.push(obj);
  });
  return newList;
}

export const createRelationshipsList = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": element.name, "label": element.name ? element.name : element.title };
    newList.push(obj);
  });
  return newList;
}

export const createCurrenciesListWithName = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": element.name, "label": element.name ? `${element.name} (${element.intl_symbol}) - ${element.country}` : element.title };
    newList.push(obj);
  });
  return newList;
}
export const createDepartmentListWithCode = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": element.hierarchy_code, "label": element.name ? element.name : element.title };
    newList.push(obj);
  });
  return newList;
}
export const createSelectScales = (data) => {
  let newList = []
  data.forEach(element => {
    let obj = { "value": getValue(element), "label": element.name ? element.name : element.title };
    newList.push(obj);
  });
  return newList;
}

export const updateById = (dataSource, data) => {
  let index = dataSource.findIndex(each => each.id === data.id);
  dataSource[index] = data;
  return dataSource;
}

export const deleteById = (dataSource, data) => {
  let index = dataSource.findIndex(each => each.id === data.id);
  dataSource.splice(index, 1);
  return dataSource;
}

/* Thin aliases kept for older call sites — new code should import
   @comps/toast directly. */
export const notifySuccess = (str) => toast.success(str);

export const notifyError = (str) => toast.error(str);

export const handleDownload = async (url, filename) => {
  // A plain <a download> is ignored by the browser for cross-origin URLs
  // (API and static asset hosts differ), so it just opens a new tab
  // instead of downloading. Fetching the file and downloading it from a
  // blob: URL forces an actual download regardless of origin.
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename || url.split('/').pop(); // Use the given filename or extract from URL
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    toast.error('Failed to download file');
  }
}

/**
 * Is the signed-in user unrestricted?
 *
 * The same rule the server applies in CheckPermission::isAdmin and
 * ScopesCaseVisibility::caseAccessFor, and the one login reports back on
 * the user as `is_admin`: a system user, or a user on a system role.
 *
 * Read from localStorage rather than the store because checkPermission
 * below is a plain function called from render bodies, not a hook — the
 * two have to answer from the same place.
 */
export const isAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return !!(user && (user.is_admin || user.is_system || (user.role && user.role.is_system)));
  } catch (e) {
    return false;
  }
}

/** Does the user's own permission list carry this key? Admin blind. */
const hasGrantedPermission = (key) => {
  try {
    const granted = JSON.parse(localStorage.getItem('permissions')) || [];
    return granted.some((each) => each.key === key);
  } catch (e) {
    return false;
  }
}

/**
 * May the user do this?
 *
 * An admin may do anything, exactly as the server has always had it:
 * CheckPermission lets a system user (or a user on a system role) past
 * every route before it looks at a single key. This side never did, so
 * an admin was shown a UI narrower than their actual access — no Create
 * button, no row actions, no dashboard — on screens whose endpoints
 * would have answered them perfectly happily.
 *
 * Anyone else is judged on the keys their role was granted.
 */
export const checkPermission = (key) => {
  if (isAdmin()) {
    return true;
  }
  return hasGrantedPermission(key);
}

/**
 * Is the signed-in user a doctor (sees only their own cases) or a
 * technician (sees every case in the clinic, whichever doctor raised
 * it)?
 *
 * Mirrors the server's rule in ScopesCaseVisibility exactly, fallback
 * and all. The screens used to ask `checkPermission('cases_create')`,
 * which read "can raise a case" as "is a doctor" - true today, but the
 * two are about to come apart, and a technician allowed to raise cases
 * would then have been misread as a doctor.
 */
export const isDoctorRole = () => {
  /*
   * Asked of an admin, this is the wrong question, and answering it the
   * usual way broke them. The admin role is granted every permission,
   * caseaccess_doctor and cases_create included, so the moment
   * checkPermission started passing an admin everything they read as a
   * doctor — and the case screen hides the technician's side of itself
   * from a doctor, so Take this case, Put on hold and Create treatment
   * plan all vanished for the one account that is supposed to be able to
   * do everything.
   *
   * The server never had this problem: caseAccessFor answers 'all' for
   * an admin and returns before it looks at the markers at all. Same
   * here — an admin is not owner-scoped, so isTechnicianRole() stays
   * true for them and they keep both halves of the screen.
   *
   * These read the granted list directly for the same reason: they ask
   * what kind of role this is, not what the user is allowed to do.
   */
  if (isAdmin()) {
    return false;
  }
  if (hasGrantedPermission('caseaccess_doctor')) {
    return true;
  }
  if (hasGrantedPermission('caseaccess_technician')) {
    return false;
  }
  // A role nobody has marked yet still behaves the way it used to.
  return hasGrantedPermission('cases_create');
}

export const isTechnicianRole = () => !isDoctorRole();

export const getStatus = (status) => {
  if (status === 0) {
    return "Pending"
  }
  if (status === 1) {
    return "Taken"
  }
  if (status === 2) {
    return "Submited"
  }
  if (status === 3) {
    return "Rejected"
  }
  if (status === 4) {
    return "Approved"
  }
  if (status === 5) {
    return "Complete"
  }
}

export const getStatusColor = (status) => {
  if (status === 0) {
    return "orange"
  }
  if (status === 1) {
    return "green"
  }
  if (status === 2) {
    return "green"
  }
  if (status === 3) {
    return "red"
  }
  if (status === 4) {
    return "green"
  }
  if (status === 5) {
    return "green"
  }
}